import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Text } from '@hopskipdrive/react-components';
import { ButtonLink } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';

import { analyticsOrganizerInvitedRider } from '../../../../api/services/Analytics';
import { shareAccess } from '../../../../api/services/DailyRides';
import { useFlags } from '../../../../contexts/LDContext';
import { useToastStore } from '../../../../shared/stores/toastStore';

import { ShareAccessForm } from './ShareAccessForm';
import { shareAccessSchema } from './validationSchema';

export const ShareAccess: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToastStore();
  const { enableDailyQueueAssignedLocations } = useFlags();

  const methods = useForm({
    resolver: yupResolver(shareAccessSchema(enableDailyQueueAssignedLocations)),
  });

  const handleSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const emails = data.emails.split(',').map((email) => email.trim());

      const payload = {
        emails: emails,
        ...(data.defaultAccountLocation && { defaultAccountLocationId: data.defaultAccountLocation.id }),
        ...(data.accountLocations?.length && { accountLocationIds: data.accountLocations.map((loc) => loc.id) }),
      };

      const response = await shareAccess(payload);

      if (response.type === 'success') {
        analyticsOrganizerInvitedRider(
          emails.length,
          emails,
          enableDailyQueueAssignedLocations ? data.accountLocations : data.defaultAccountLocation
        );
        navigate(-1);
        addToast({
          severity: 'success',
          message: response.data.message,
        });
      } else {
        response.error?.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
      }
    } catch (error) {
      addToast({ severity: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={true} maxWidth="md" data-testid="share-access-modal">
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Share access to Daily Queue
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Text variant="bodyS">
          The people you send this will be notified and invited to join RideIQ as contacts for this school.
          <br />
          *Reminder: Parents should not have access to the Daily Queue Dashboard.
        </Text>
        <FormProvider {...methods}>
          <ShareAccessForm handleSubmit={handleSubmit} />
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <ButtonLink color="tertiary" disabled={isSubmitting} to="..">
          Cancel
        </ButtonLink>
        <Button
          type="submit"
          data-testid="share-button"
          color="primary"
          form="share-access-form"
          loading={isSubmitting}
        >
          {isSubmitting ? 'Sharing...' : 'Share'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
